import React, { ReactNode, useCallback, useRef } from 'react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import preval from 'preval.macro'
import Skeleton from '@mui/material/Skeleton'

import Button, { ButtonProps } from 'components/Button'
import ButtonBase from 'components/Button/ButtonBase'
import ButtonConnectMetamask from 'components/Button/ButtonConnectMetamask'
import Typography from 'components/Typography'
import ImgPure from 'components/Img'
import { route } from 'routes'
import { defaultPageTitle, values } from 'appConstants'
import { NavbarChildProps, NavbarProps } from '.'
import { DialogNames } from 'duck/DialogDuck'

import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './Navbar.module.scss'
import ImgLogo from 'assets/images/logo-artmine-playform@3x.png'

const NavbarDesktopSkeleton: React.FC<{}> = props => {
  return (
    <div className={styles.NavbarDesktop}>
      <div
        className={clsx(
          stylesGlobal.DisplayFlex,
          stylesGlobal.AlignItemsCenter,
          stylesGlobal.HeightFull,
          styles.NavbarDesktopInner
        )}
      >
        <div className={clsx(stylesGlobal.DisplayBlock, styles.Logo)}>
          <ImgPure
            src={ImgLogo}
            className={clsx(
              stylesGlobal.DisplayBlock,
              stylesGlobal.WidthFull,
              stylesGlobal.PointerEventsNone,
              stylesGlobal.UserSelectNone
            )}
            title={defaultPageTitle}
            alt={defaultPageTitle}
          />
        </div>

        <div className={stylesGlobal.MarginLeftAuto}>
          <ul className={clsx(stylesGlobal.DisplayFlex, styles.Menu)}>
            <li className={clsx(stylesGlobal.PaddingX16)}>
              <Skeleton width={48} />
            </li>

            <li className={clsx(stylesGlobal.PaddingX16)}>
              <Skeleton width={48} />
            </li>

            <li className={clsx(stylesGlobal.PaddingX16)}>
              <Skeleton width={48} />
            </li>

            <li className={clsx(stylesGlobal.PaddingX16)}>
              <Skeleton width={48} />
            </li>

            <li className={clsx(stylesGlobal.PaddingX16)}>
              <Skeleton width={48} />
            </li>

            <li className={stylesGlobal.MarginLeft16}>
              <Skeleton width={140} />
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

type MenuItemProps = Pick<ButtonProps, 'href' | 'target' | 'variant' | 'onClick' | 'disabled'> & {
  className?: string
  label: ReactNode
  to?: string
}

const MenuItem: React.FC<MenuItemProps> = props => {
  const { className, href, label, target, to, variant, disabled, onClick } = props

  return (
    <li className={className}>
      {variant ? (
        <Button
          disabled={disabled}
          variant={variant}
          to={to}
          href={href}
          onClick={onClick}
          target={target}
        >
          <Typography fontWeight="semibold" fontSize="inherit" component="span">
            {label}
          </Typography>
        </Button>
      ) : (
        <ButtonBase
          disabled={disabled}
          className={styles.MenuItemButtonBase}
          onClick={onClick}
          to={to}
          href={href}
          target={target}
        >
          <Typography fontWeight="semibold" fontSize="inherit" component="span">
            {label}
          </Typography>
        </ButtonBase>
      )}
    </li>
  )
}

const NavbarDesktop: React.FC<NavbarChildProps & Pick<NavbarProps, 'addDialog'>> = props => {
  const { loading, userAddressShort, onClickConnectMetamask, addDialog } = props

  const dateTimeStamp = useRef(preval`module.exports = new Date().toLocaleString();`)

  const onContextMenu = useCallback(
    e => {
      e.preventDefault()
      addDialog({
        [DialogNames.INFORMATION]: {
          dialogName: DialogNames.INFORMATION,
          title: `Art Mine By Playform`,
          content: `BUILD DATE: ${dateTimeStamp.current}`
        }
      })
    },
    [addDialog]
  )

  return (
    <div className={styles.NavbarDesktop}>
      <div
        className={clsx(
          stylesGlobal.DisplayFlex,
          stylesGlobal.AlignItemsCenter,
          stylesGlobal.HeightFull,
          styles.NavbarDesktopInner
        )}
      >
        <div>
          <Link
            onContextMenu={onContextMenu}
            to={route.ROOT.getUrl()}
            className={clsx(stylesGlobal.DisplayBlock, styles.Logo)}
          >
            <ImgPure
              src={ImgLogo}
              className={clsx(
                stylesGlobal.DisplayBlock,
                stylesGlobal.WidthFull,
                stylesGlobal.PointerEventsNone,
                stylesGlobal.UserSelectNone
              )}
              title={defaultPageTitle}
              alt={defaultPageTitle}
            />
          </Link>
        </div>

        <div className={stylesGlobal.MarginLeftAuto}>
          <ul className={clsx(stylesGlobal.DisplayFlex, styles.Menu)}>
            <MenuItem
              label="Explore"
              to={route.MINE_PROJECTS.getUrl({}, { ...values.BASE_PROJECT_LIST_PARAM })}
            />
            <MenuItem label="About" to={route.ABOUT.getUrl()} />
            <MenuItem label="My Collection" to={route.COLLECTIONS.getUrl()} />
            <MenuItem label="Create" href={values.PLAYFORM_CREATE_URL} target="_blank" />
            <MenuItem label="Contact Us" to={route.CONTACT.getUrl()} />

            <li className={stylesGlobal.MarginLeft16}>
              <ButtonConnectMetamask
                loading={loading}
                userAddressShort={userAddressShort}
                onClick={onClickConnectMetamask}
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

const NavbarDesktopContainer: React.FC<
  NavbarChildProps & Pick<NavbarProps, 'addDialog'>
> = props => {
  if (props.loading) return <NavbarDesktopSkeleton />

  return (
    <NavbarDesktop
      addDialog={props.addDialog}
      userAddressShort={props.userAddressShort}
      loading={props.loading}
      onClickConnectMetamask={props.onClickConnectMetamask}
    />
  )
}

export default NavbarDesktopContainer
